<script setup lang="ts">
import { twMerge } from "tailwind-merge";
import { computed, ref, watchEffect } from "vue";
import { useI18n } from "vue-i18n";
import { RouterLink, useRoute } from "vue-router";

import { useUser } from "@/composables/api/useUser";

import ShareArrowIcon from "@/components/icons/ShareArrowIcon.vue";
import Button from "@/components/ui/Button.vue";
import CopyUrlInput from "@/components/ui/CopyUrlInput.vue";
import Dialog from "@/components/ui/Dialog.vue";

const { data: user } = useUser();

const { t } = useI18n();
const route = useRoute();

const modalParam = computed(() =>
  typeof route.query.modal === "string" ? route.query.modal : undefined,
);

const isTermsOpen = ref(false);
const isCookieModalOpen = ref(false);
const isCopyUrlOpen = ref(false);

function openTerms() {
  isTermsOpen.value = true;
}

function openCookies() {
  isCookieModalOpen.value = true;
}

function openLegalMentions() {
  window.open(t("footer.legalMentionsUrl"), "_blank");
}

function openPersonalData() {
  window.open(t("footer.personalDataUrl"), "_blank");
}

function handleShare() {
  // Check if it's a mobile device (width <= 1024px)
  const isMobileDevice = window.innerWidth <= 1024;

  // Only use native share on mobile devices
  if (navigator.share && isMobileDevice) {
    navigator.share({
      title: t("nativeShare.title"),
      text: t("nativeShare.text"),
      url: t("nativeShare.url"),
    });
  } else {
    isCopyUrlOpen.value = true;
  }
}

const helpLinks = [
  { label: "footer.howItWorks", to: "how-it-works", hash: "#how-it-works" },
  { label: "footer.productsList", to: "how-it-works", hash: "#eligible-products" },
  { label: "footer.claim", to: "how-it-works", hash: "#claim" },
  { label: "footer.help", to: "how-it-works", hash: "#help" },
];

const legalLinksGalbani = [
  { label: "footer.terms", onClick: openTerms },
  { label: "footer.legalMentions", onClick: openLegalMentions },
  { label: "footer.manageCookies", onClick: openCookies },
  { label: "footer.personalData", onClick: openPersonalData },
];

const legalLinksLactel = computed(() => {
  if (user.value?.current_mechanic === "lactel") {
    return [
      { label: "footer.terms", url: t("footer.terms.url") },
      { label: "footer.legalMentions", url: t("footer.legalMentions.url") },
      { label: "footer.manageCookies", url: t("footer.manageCookies.url") },
      { label: "footer.personalData", url: t("footer.personalData.url") },
    ];
  } else {
    return [];
  }
});

watchEffect(() => {
  if (modalParam.value === "Terms") {
    openTerms();
  }

  if (modalParam.value === "CookiePolicy") {
    openCookies();
  }
});
</script>

<template>
  <footer
    :class="
      twMerge(
        'bg-galbani flex w-full flex-col items-center gap-10 px-6 pt-10 pb-24 lg:pt-16',
        user?.current_mechanic === 'lactel' && 'bg-lactel-blue',
      )
    "
    role="contentinfo"
  >
    <Dialog v-model:open="isTermsOpen" @update:open="isTermsOpen = $event">
      <template #content>
        <div class="flex w-full flex-col gap-5">
          <h1
            :class="
              twMerge(
                'text-galbani text-2xl font-semibold',
                user?.current_mechanic === 'lactel' && 'text-lactel-blue text-shadow-lactel-blue',
              )
            "
          >
            {{ $t("dialog.terms.title") }}
          </h1>

          <div
            class="text-secondary flex w-full flex-col gap-4 text-base font-medium [&>a]:font-medium [&>a]:underline"
            v-html="$t('dialog.terms.content')"
          />
        </div>
      </template>
    </Dialog>

    <Dialog v-model:open="isCookieModalOpen" @update:open="isCookieModalOpen = $event">
      <template #content>
        <div class="flex w-full flex-col gap-5">
          <h1
            :class="
              twMerge(
                'text-galbani text-2xl font-semibold',
                user?.current_mechanic === 'lactel' && 'text-lactel-blue',
              )
            "
          >
            {{ $t("dialog.cookies.title") }}
          </h1>

          <div
            class="text-secondary flex w-full flex-col gap-4 text-base font-medium"
            v-html="$t('dialog.cookies.content')"
          />
        </div>
      </template>
    </Dialog>

    <Dialog v-model:open="isCopyUrlOpen" @update:open="isCopyUrlOpen = $event">
      <template #content>
        <div class="flex w-full flex-col gap-5">
          <h1
            :class="
              twMerge(
                'text-galbani text-2xl font-semibold',
                user?.current_mechanic === 'lactel' && 'text-lactel-blue',
              )
            "
          >
            {{ $t("dialog.copyUrl.title") }}
          </h1>

          <p
            v-if="user?.current_mechanic === 'lactel'"
            class="text-secondary text-[18px] font-medium"
            v-html="$t('dialog.copyUrl.subtitle')"
          />

          <CopyUrlInput
            :value="$t('dialog.copyUrl.url')"
            :successMessage="$t('dialog.copyUrl.success')"
          />
        </div>
      </template>
    </Dialog>

    <Button
      variant="primary"
      aria-label="Partager à mes proches"
      class="focus:ring-white/25"
      @click="handleShare"
    >
      {{ $t("footer.shareButton") }}
      <ShareArrowIcon aria-hidden="true" />
    </Button>

    <nav
      class="flex w-full flex-col items-center gap-5 lg:gap-6"
      aria-label="Liens d'aide et légaux"
    >
      <ul class="flex w-full list-none flex-col items-center gap-5">
        <li v-for="link in helpLinks" :key="link.label" class="flex items-center">
          <!-- eslint-disable vue/no-v-text-v-html-on-component -->
          <RouterLink
            :to="{ name: link.to, hash: link.hash }"
            class="text-center text-lg font-medium text-white"
            v-html="$t(link.label)"
          />
          <!-- eslint-enable vue/no-v-text-v-html-on-component -->
        </li>
      </ul>

      <hr class="h-px w-full border-0 bg-white lg:!my-4" aria-hidden="true" />

      <ul
        v-if="user?.current_mechanic === 'galbani'"
        class="flex w-full list-none flex-col items-center gap-5 lg:flex-row lg:justify-center lg:gap-6"
      >
        <li v-for="link in legalLinksGalbani" :key="link.label">
          <button
            type="button"
            class="cursor-pointer text-center text-base text-white"
            :onClick="link.onClick"
          >
            {{ $t(link.label) }}
          </button>
        </li>
      </ul>

      <ul
        v-if="user?.current_mechanic === 'lactel'"
        class="flex w-full list-none flex-col items-center gap-5 lg:flex-row lg:justify-center lg:gap-6"
      >
        <li v-for="link in legalLinksLactel" :key="link.label">
          <a
            :href="link.url"
            target="_blank"
            rel="noopener noreferrer"
            type="button"
            class="cursor-pointer text-center text-base text-white"
          >
            {{ $t(link.label) }}
          </a>
        </li>
      </ul>
    </nav>

    <div
      class="flex max-w-xs flex-col gap-1.5 text-center text-sm leading-[140%] text-white/80"
      v-html="$t('footer.disclaimer')"
    />
  </footer>
</template>
